/**
 * Metric colour resolution — a LEAF module (no imports) so both the widget
 * registry and the renderers (CustomWidget) can share it without forming an
 * import cycle (widgetRegistry ⇄ CustomWidget via ScopedCustomWidget).
 *
 * A metric's colour is one visual decision that drives its target bar, sparkline
 * and ring together. Beyond the fixed palette there are sentinels:
 *   NO_COLOR      — draw in neutral grey (a calm/monochrome card)
 *   COMPANY_COLOR — track the tenant's brand primary (not a frozen hex)
 *   GOAL_COLOR    — green when value ≥ target, red when below (needs progress)
 */

/* "No colour" — a deliberate neutral choice, distinct from "unset" (null, which
   falls back to the cycling accent). */
export const NO_COLOR = "none";
export const NEUTRAL_METRIC_COLOR = "#98A2B3";

/* "Company colour" — resolved to the live brand primary at paint time, so the
   metric restyles automatically if the company changes its colour. */
export const COMPANY_COLOR = "company";

/* "By goal" — green if the metric has hit its target, red if not. */
export const GOAL_COLOR = "goal";
export const GOAL_MET_COLOR = "#12A150";
export const GOAL_MISS_COLOR = "#F04438";

/**
 * @param {string|null|undefined} color
 * @param {string} fallback
 * @param {string|null} [companyColor]
 * @param {{ value?: number, target?: number }|null} [progress] — required for GOAL_COLOR
 */
export const resolveMetricColor = (color, fallback, companyColor = null, progress = null) => {
  if (color === NO_COLOR) return NEUTRAL_METRIC_COLOR;
  if (color === COMPANY_COLOR) return companyColor || fallback;
  if (color === GOAL_COLOR) {
    /* Without live progress, keep the sentinel so MetricBlock can resolve
       after it knows value / target. */
    if (!progress) return GOAL_COLOR;
    const target = Number(progress?.target) || 0;
    if (!(target > 0)) return NEUTRAL_METRIC_COLOR;
    const value = Number(progress?.value) || 0;
    return value >= target ? GOAL_MET_COLOR : GOAL_MISS_COLOR;
  }
  return color || fallback;
};

const HEX = /^#[0-9a-fA-F]{6}$/;

/** True for a persistable metric colour (hex or Appearance sentinels). */
export const isAllowedMetricColor = (color) =>
  typeof color === "string" &&
  (color === NO_COLOR || color === COMPANY_COLOR || color === GOAL_COLOR || HEX.test(color));
